import React, { useEffect, useState, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import AppShell from '../../components/layout/AppShell';
import { financeApi } from '../../services/api';
import { fmtPKR, fmtNum } from '../../utils/helpers';
import { Spinner } from '../../components/common';

const COLORS = ['#1B2A4A','#F59E0B','#10B981','#EF4444','#3B82F6','#8B5CF6'];

export default function FinanceDashboard() {
  const [data, setData]       = useState(null);
  const [report, setReport]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [rptPeriod, setRptPeriod] = useState('day');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, rev] = await Promise.all([
        financeApi.getFinanceDashboard().catch(() => ({ success:false, data:null })),
        financeApi.getRevenueReport({ groupBy: rptPeriod }),
      ]);
      if (dash.success) setData(dash.data);
      if (rev.success)  setReport(rev.data);
    } finally { setLoading(false); }
  }, [rptPeriod]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <AppShell title="Finance Dashboard"><Spinner /></AppShell>;

  const aging = data?.aging || {};

  return (
    <AppShell title="Finance Dashboard" actions={
      <button className="btn btn-outline btn-sm" onClick={load}>↻ Refresh</button>
    }>
      {/* KPI Row */}
      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <KpiCard label="Receivable (Pending)" value={fmtPKR(data?.invoices?.pending)}        color="var(--warning)" count={data?.invoices?.pending_count} />
        <KpiCard label="Overdue Invoices"     value={fmtPKR(data?.invoices?.overdue)}         color="var(--danger)"  count={data?.invoices?.overdue_count} />
        <KpiCard label="Collected MTD"        value={fmtPKR(data?.invoices?.collected_mtd)}   color="var(--success)" />
        <KpiCard label="COD Pending (7d)"     value={fmtPKR(data?.cod?.find(c=>c.deposit_status==='PENDING')?.total)} color="var(--amber)" />
        <KpiCard label="COD Deposited (7d)"   value={fmtPKR(data?.cod?.find(c=>c.deposit_status==='DEPOSITED')?.total)} color="var(--success)" />
      </div>

      {/* Charts row */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
        {/* Revenue trend */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Revenue Trend</span>
            <select className="form-control" style={{ width:100, padding:'4px 8px' }}
              value={rptPeriod} onChange={e => setRptPeriod(e.target.value)}>
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={report} margin={{ top:4, right:8, bottom:0, left:0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="period" tick={{ fontSize:10 }} tickFormatter={d => d ? String(d).slice(5,10) : ''} />
                <YAxis tick={{ fontSize:10 }} tickFormatter={v=>`${Math.round(v/1000)}k`} />
                <Tooltip formatter={v => fmtPKR(v)} />
                <Bar dataKey="gross_revenue" name="Revenue" fill="#1B2A4A" radius={[3,3,0,0]} />
                <Bar dataKey="freight"        name="Freight"  fill="#F59E0B" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue by service */}
        <div className="card">
          <div className="card-header"><span className="card-title">Revenue by Service</span></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data?.revenueByService||[]} dataKey="revenue" nameKey="service_type"
                  cx="50%" cy="50%" outerRadius={75} label={({service_type,percent})=>`${service_type} ${(percent*100).toFixed(0)}%`}
                  labelLine={false} fontSize={10}>
                  {(data?.revenueByService||[]).map((_,i) => <Cell key={i} fill={COLORS[i%COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={v => fmtPKR(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Aging analysis */}
      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-header"><span className="card-title">Receivables Aging Analysis</span></div>
        <div className="card-body">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16 }}>
            <AgingBucket label="Due in 30 days"   value={aging.due_30}      color="var(--info)" />
            <AgingBucket label="Overdue 1–30 days" value={aging.overdue_30} color="var(--warning)" />
            <AgingBucket label="Overdue 31–60 days" value={aging.overdue_60} color="var(--amber)" />
            <AgingBucket label="Overdue 60+ days"  value={aging.overdue_90plus} color="var(--danger)" />
          </div>
          <div style={{ marginTop:16 }}>
            <div style={{ height:16, borderRadius:8, background:'var(--gray-100)', overflow:'hidden', display:'flex' }}>
              {[
                { v:aging.due_30,      color:'var(--info)' },
                { v:aging.overdue_30,  color:'var(--warning)' },
                { v:aging.overdue_60,  color:'var(--amber)' },
                { v:aging.overdue_90plus, color:'var(--danger)' },
              ].map((b, i) => {
                const total = [aging.due_30,aging.overdue_30,aging.overdue_60,aging.overdue_90plus].reduce((a,x)=>a+parseFloat(x||0),0);
                const pct = total ? (parseFloat(b.v||0)/total)*100 : 0;
                return <div key={i} style={{ width:`${pct}%`, background:b.color, transition:'width .3s' }} />;
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Shipment revenue report table */}
      <div className="card">
        <div className="card-header"><span className="card-title">Revenue Report — Current Period</span></div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Period</th><th>Shipments</th><th>Delivered</th><th>RTO</th><th>Gross Revenue</th><th>Freight</th><th>Tax</th><th>COD Fees</th></tr>
            </thead>
            <tbody>
              {report.length ? report.map((r, i) => (
                <tr key={i}>
                  <td style={{ fontFamily:'monospace', fontSize:'.8rem' }}>{r.period ? String(r.period).slice(0,10) : '—'}</td>
                  <td>{fmtNum(r.shipments)}</td>
                  <td style={{ color:'var(--success)', fontWeight:500 }}>{fmtNum(r.delivered)}</td>
                  <td style={{ color:'var(--danger)' }}>{fmtNum(r.rto)}</td>
                  <td style={{ fontWeight:600 }}>{fmtPKR(r.gross_revenue)}</td>
                  <td>{fmtPKR(r.freight)}</td>
                  <td>{fmtPKR(r.tax)}</td>
                  <td>{fmtPKR(r.cod_fees)}</td>
                </tr>
              )) : (
                <tr><td colSpan={8} style={{ textAlign:'center', padding:32, color:'var(--gray-400)' }}>No data for selected period</td></tr>
              )}
            </tbody>
            {report.length > 0 && (
              <tfoot>
                <tr style={{ background:'var(--gray-50)', fontWeight:700 }}>
                  <td>Total</td>
                  <td>{fmtNum(report.reduce((a,r)=>a+parseInt(r.shipments||0),0))}</td>
                  <td>{fmtNum(report.reduce((a,r)=>a+parseInt(r.delivered||0),0))}</td>
                  <td>{fmtNum(report.reduce((a,r)=>a+parseInt(r.rto||0),0))}</td>
                  <td>{fmtPKR(report.reduce((a,r)=>a+parseFloat(r.gross_revenue||0),0))}</td>
                  <td>{fmtPKR(report.reduce((a,r)=>a+parseFloat(r.freight||0),0))}</td>
                  <td>{fmtPKR(report.reduce((a,r)=>a+parseFloat(r.tax||0),0))}</td>
                  <td>{fmtPKR(report.reduce((a,r)=>a+parseFloat(r.cod_fees||0),0))}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </AppShell>
  );
}

function KpiCard({ label, value, color, count }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ fontSize:'1.3rem', color }}>{value}</div>
      {count !== undefined && <div style={{ fontSize:'.75rem', color:'var(--gray-400)', marginTop:4 }}>{fmtNum(count)} invoices</div>}
    </div>
  );
}

function AgingBucket({ label, value, color }) {
  return (
    <div style={{ textAlign:'center', padding:12, border:`2px solid ${color}20`, borderRadius:8 }}>
      <div style={{ fontSize:'1.2rem', fontWeight:700, color }}>{fmtPKR(value)}</div>
      <div style={{ fontSize:'.75rem', color:'var(--gray-500)', marginTop:4 }}>{label}</div>
    </div>
  );
}
